import { useState } from "react";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AutoTextarea } from "@/components/AutoTextarea";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";

export function ExtractWordsDialog({ lang, onOpenChange }) {
  const invalidate = useInvalidate();
  const [text, setText] = useState("");
  const [found, setFound] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/languages/${lang.id}/ai/extract-words`, { text });
      setFound(data.words);
      setPicked(Object.fromEntries(data.words.map((w) => [w.word, true])));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/languages/${lang.id}/words/bulk`, { words: found.filter((w) => picked[w.word]) });
      invalidate(["words", lang.id], ["language", lang.id], ["languages", lang.universe_id]);
      toast.success(`${data.added} words added to the dictionary`);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto lf-scroll" data-testid="extract-words-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Find words in a note</DialogTitle>
          <DialogDescription>Paste any text. The scribe picks out {lang.name} words with their meanings, and you choose which to keep.</DialogDescription>
        </DialogHeader>
        {found === null ? (
          <AutoTextarea value={text} onChange={(e) => setText(e.target.value)} maxVh={50} className="min-h-[220px] leading-relaxed"
            placeholder={`In ${lang.name}, "kesh" means fire…`} data-testid="extract-words-input" />
        ) : found.length === 0 ? (
          <p className="text-sm text-[#A8A090]" data-testid="extract-words-none">No new {lang.name} words found in that text.</p>
        ) : (
          <div className="space-y-1.5 rounded-md border border-[#2D3442] bg-[#0F131A] p-2" data-testid="extract-words-results">
            {found.map((w) => (
              <label key={w.word} className="flex cursor-pointer items-start gap-3 rounded px-2 py-2 hover:bg-[#1A202C]">
                <Checkbox className="mt-1" checked={!!picked[w.word]} onCheckedChange={(v) => setPicked({ ...picked, [w.word]: !!v })} data-testid={`extract-word-${w.word}`} />
                <div className="text-sm">
                  <span className="font-display text-lg text-[#F3EBDD]">{w.word}</span>
                  {w.pronunciation && <span className="ml-2 text-xs text-[#A8A090]">/{w.pronunciation}/</span>}
                  {w.part_of_speech && <span className="ml-2 text-xs italic text-[#14B8A6]">{w.part_of_speech}</span>}
                  <div className="text-[#E8DFD1]">{w.meaning}</div>
                  {w.example && <div className="text-xs italic text-[#A8A090]">“{w.example}”</div>}
                </div>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          {found !== null && <Button variant="ghost" onClick={() => setFound(null)} data-testid="extract-words-back">Back</Button>}
          {found === null ? (
            <Button onClick={run} disabled={busy || text.trim().length < 3} data-testid="extract-words-run"><Wand2 className="h-4 w-4 mr-1.5" />{busy ? "Reading…" : "Find words"}</Button>
          ) : (
            <Button onClick={add} disabled={busy || !found.some((w) => picked[w.word])} data-testid="extract-words-add">{busy ? "Adding…" : `Add ${found.filter((w) => picked[w.word]).length} words`}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
